import api from '@/utils/api';

interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
}

interface FacebookPage {
  id: string;
  name: string;
  category: string;
  picture?: string;
  has_leadgen_access: boolean;
  access_token: string;
}

interface ConnectedPage {
  page_id: string;
  page_name: string;
  connected_at: string;
  facebook_user_name?: string;
  facebook_user_email?: string;
}

interface FacebookLeadForm {
  id: number;
  uuid: string;
  form_id: string;
  form_name: string;
  form_status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  page_id: string;
  created_time?: string;
  field_mapping?: any;
  last_sync_at?: string;
  total_leads_synced: number;
  is_active: boolean;
  formFields?: FacebookFormField[];
}

interface FacebookFormField {
  id: number;
  uuid: string;
  facebook_field_key: string;
  facebook_field_label?: string;
  facebook_field_type: string; // Any field type from Facebook API
  is_required: boolean;
  field_order: number;
  mapped_crm_column?: string;
  is_active: boolean;
}

class FacebookPagesService {
  private readonly baseUrl = '/integrations/facebook-pages';

  /**
   * Generate Facebook Pages OAuth URL
   */
  async generateOAuthUrl(): Promise<ApiResponse<{ oauth_url: string; state: string }>> {
    try {
      const response = await api.req<{ oauth_url: string; state: string }>({
        url: `${this.baseUrl}/oauth/connect`,
        method: api.Method.GET,
      });
      return response as ApiResponse<{ oauth_url: string; state: string }>;
    } catch (error) {
      console.error('Error generating Facebook Pages OAuth URL:', error);
      throw error;
    }
  }

  /**
   * Get user's Facebook Pages
   */
  async getPages(): Promise<ApiResponse<FacebookPage[]>> {
    try {
      const response = await api.req<FacebookPage[]>({
        url: `${this.baseUrl}/pages`,
        method: api.Method.GET,
      });
      return response as ApiResponse<FacebookPage[]>;
    } catch (error) {
      console.error('Error getting Facebook Pages:', error);
      throw error;
    }
  }

  /**
   * Subscribe to Facebook Page for lead retrieval
   */
  async subscribePage(pageId: string, pageAccessToken: string, pageName: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.baseUrl}/subscribe`,
        method: api.Method.POST,
        body: {
          page_id: pageId,
          page_access_token: pageAccessToken,
          page_name: pageName
        }
      });
      return response as ApiResponse<any>;
    } catch (error: any) {
      console.error('Error subscribing to Facebook Page:', error);
      // Extract error message from the error object
      const errorMessage = error.message || 'Failed to connect page';
      // Return a properly formatted error response
      return {
        success: false,
        message: errorMessage
      } as ApiResponse<any>;
    }
  }

  /**
   * Unsubscribe from Facebook Page
   */
  async unsubscribePage(): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.baseUrl}/unsubscribe`,
        method: api.Method.POST,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error unsubscribing from Facebook Page:', error);
      throw error;
    }
  }

  /**
   * Check if Facebook is authenticated (IntegrationSetting exists)
   */
  async checkFacebookAuth(): Promise<ApiResponse<{
    is_authenticated: boolean;
    facebook_user?: {
      id: string;
      name: string;
      email: string;
      picture?: string;
    }
  }>> {
    try {
      const response = await api.req<{
        is_authenticated: boolean;
        facebook_user?: {
          id: string;
          name: string;
          email: string;
          picture?: string;
        }
      }>({
        url: `${this.baseUrl}/auth-status`,
        method: api.Method.GET,
      });
      return response as ApiResponse<{
        is_authenticated: boolean;
        facebook_user?: {
          id: string;
          name: string;
          email: string;
          picture?: string;
        }
      }>;
    } catch (error) {
      console.error('Error checking Facebook auth status:', error);
      throw error;
    }
  }

  /**
   * Get connected Facebook Page
   */
  async getConnectedPage(): Promise<ApiResponse<ConnectedPage | null>> {
    try {
      const response = await api.req<ConnectedPage | null>({
        url: `${this.baseUrl}/connected`,
        method: api.Method.GET,
      });
      return response as ApiResponse<ConnectedPage | null>;
    } catch (error) {
      console.error('Error getting connected Facebook Page:', error);
      throw error;
    }
  }

  /**
   * Disconnect Facebook Pages integration
   */
  async disconnect(): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.baseUrl}/disconnect`,
        method: api.Method.POST,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error disconnecting Facebook Pages integration:', error);
      throw error;
    }
  }

  /**
   * Test Facebook Pages connection
   */
  async testConnection(): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.baseUrl}/test`,
        method: api.Method.POST,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error testing Facebook Pages connection:', error);
      throw error;
    }
  }

  /**
   * Get Facebook Lead Gen Forms
   */
  async getLeadForms(cachedOnly: boolean = false): Promise<ApiResponse<FacebookLeadForm[]>> {
    try {
      const url = cachedOnly
        ? `${this.baseUrl}/forms?cached_only=true`
        : `${this.baseUrl}/forms`;

      const response = await api.req<FacebookLeadForm[]>({
        url,
        method: api.Method.GET,
      });
      return response as ApiResponse<FacebookLeadForm[]>;
    } catch (error) {
      console.error('Error getting Facebook Lead Gen Forms:', error);
      throw error;
    }
  }

  /**
   * Save field mapping for a specific form
   */
  async saveFormMapping(formId: string, fieldMapping: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.baseUrl}/forms/${formId}/mapping`,
        method: api.Method.POST,
        body: {
          field_mapping: fieldMapping
        }
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error saving form mapping:', error);
      throw error;
    }
  }

  /**
   * Preview leads from Facebook for a specific form with date range filters
   */
  async previewLeads(formId: string, fromDate?: string, toDate?: string, limit?: number): Promise<ApiResponse<{
    count: number;
    newLeads: number;
    duplicates: number;
    leads: any[];
  }>> {
    try {
      // Build query parameters
      const params: Record<string, string> = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (limit && limit > 0) params.limit = limit.toString();

      const queryString = Object.keys(params).length > 0
        ? '?' + new URLSearchParams(params).toString()
        : '';

      const response = await api.req<{
        count: number;
        newLeads: number;
        duplicates: number;
        leads: any[];
      }>({
        url: `${this.baseUrl}/forms/${formId}/preview-leads${queryString}`,
        method: api.Method.GET,
      });
      return response as ApiResponse<{
        count: number;
        newLeads: number;
        duplicates: number;
        leads: any[];
      }>;
    } catch (error) {
      console.error('Error previewing leads:', error);
      throw error;
    }
  }

  /**
   * Sync leads from Facebook for a specific form with date range filters
   */
  async syncLeads(formId: string, fromDate?: string, toDate?: string, limit?: number): Promise<ApiResponse<{
    synced: number;
    duplicates: number;
    errors: number;
    details: any[];
  }>> {
    try {
      // Build request body with date range filters
      const body: Record<string, any> = {};
      if (fromDate) body.from_date = fromDate;
      if (toDate) body.to_date = toDate;
      if (limit && limit > 0) body.limit = limit;

      const response = await api.req<{
        synced: number;
        duplicates: number;
        errors: number;
        details: any[];
      }>({
        url: `${this.baseUrl}/forms/${formId}/sync-leads`,
        method: api.Method.POST,
        body: body,
      });
      return response as ApiResponse<{
        synced: number;
        duplicates: number;
        errors: number;
        details: any[];
      }>;
    } catch (error) {
      console.error('Error syncing leads:', error);
      throw error;
    }
  }

  /**
   * Toggle is_active status for a Facebook Lead Gen Form
   */
  async toggleFormActive(formId: string, isActive: boolean): Promise<ApiResponse<{ form_id: string; is_active: boolean }>> {
    try {
      const response = await api.req<{ form_id: string; is_active: boolean }>({
        url: `${this.baseUrl}/forms/${formId}/toggle-active`,
        method: api.Method.PATCH,
        body: { is_active: isActive }
      });
      return response as ApiResponse<{ form_id: string; is_active: boolean }>;
    } catch (error) {
      console.error('Error toggling form active status:', error);
      throw error;
    }
  }

  /**
   * Refresh a single form's fields from Facebook
   */
  async refreshForm(formId: string): Promise<ApiResponse<FacebookLeadForm>> {
    try {
      const response = await api.req<FacebookLeadForm>({
        url: `${this.baseUrl}/forms/${formId}/refresh`,
        method: api.Method.POST,
      });
      return response as ApiResponse<FacebookLeadForm>;
    } catch (error) {
      console.error('Error refreshing form:', error);
      throw error;
    }
  }
}

const facebookPagesService = new FacebookPagesService();
export default facebookPagesService;